import { useCallback, useState } from "react";

/**
 * useCompareStream
 *
 * Drives the five compare phases (Auth, Source, Target, Refs, Diff) in
 * wizard step 1, so the user sees progress while the backend runs the
 * operations sequentially.
 *
 * Before each await the phase is set to "active", then to "done" (or
 * "error" if the step throws). Duration is logged in seconds so the UI can
 * render it next to the phase.
 *
 * startCompare(api, params)
 *   - api:    the helpers.api() function (or a compatible fetch wrapper)
 *   - params: {
 *       sourceInstanceId, targetInstanceId, sourcePackageId,
 *       fetchAuth, fetchSource, fetchTarget, fetchRefs, computeDiff,
 *     }
 *     Each fetch* function is optional; if it is not set, the phase still
 *     runs as a no-op so the UI stays consistent.
 */

const PHASE_DEFS = [
  { id: "auth", label: "Authentifizierung" },
  { id: "source", label: "Quelle laden" },
  { id: "target", label: "Ziel laden" },
  { id: "refs", label: "Referenzen" },
  { id: "diff", label: "Diff berechnen" },
];

function initialPhases() {
  return PHASE_DEFS.map((p) => ({
    id: p.id,
    label: p.label,
    status: "waiting",
    detail: "",
    duration: null,
    error: null,
  }));
}

export function useCompareStream() {
  const [phases, setPhases] = useState(initialPhases);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setPhases(initialPhases());
    setRunning(false);
    setError(null);
  }, []);

  const setPhase = useCallback((id, patch) => {
    setPhases((prev) => prev.map((ph) => (ph.id === id ? { ...ph, ...patch } : ph)));
  }, []);

  const startCompare = useCallback(
    async (api, params = {}) => {
      if (typeof api !== "function") {
        throw new Error("useCompareStream.startCompare benötigt eine api()-Funktion");
      }
      const handlers = {
        auth: params.fetchAuth,
        source: params.fetchSource,
        target: params.fetchTarget,
        refs: params.fetchRefs,
        diff: params.computeDiff,
      };

      setRunning(true);
      setError(null);
      setPhases(initialPhases());
      const collected = {};

      try {
        for (const def of PHASE_DEFS) {
          const id = def.id;
          const fn = handlers[id];
          const t0 = Date.now();
          setPhase(id, { status: "active", detail: "" });
          try {
            const out = typeof fn === "function" ? await fn(api, params, collected) : null;
            collected[id] = out;
            const dur = `${Math.round((Date.now() - t0) / 100) / 10}s`;
            setPhase(id, { status: "done", duration: dur });
          } catch (phaseErr) {
            const dur = `${Math.round((Date.now() - t0) / 100) / 10}s`;
            setPhase(id, {
              status: "error",
              duration: dur,
              error: phaseErr?.message || String(phaseErr),
              detail: phaseErr?.message || "Fehler",
            });
            throw phaseErr;
          }
        }
        return collected;
      } catch (e) {
        setError(e?.message || String(e));
        throw e;
      } finally {
        setRunning(false);
      }
    },
    [setPhase]
  );

  return { phases, running, error, startCompare, setPhase, reset };
}

export default useCompareStream;
